import { useCallback } from "react";
import { toast } from "sonner";
import { Share as CapShare } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";

/**
 * useFeedShare — encapsulates the 3-tier share fallback that's shared
 * between the FeedPage's "Share current video" button and the
 * FeedPlayerOverlay's bottom-bar share action. Lives in
 * /app/frontend/src/pages/feed/useFeedShare.js.
 *
 * Tier order is intentional and matched to platform-specific UX:
 *   1. Capacitor `Share` plugin → native iOS share sheet (best UX inside
 *      the iPhone binary). Static import (not dynamic) because the web
 *      build never loaded the dynamic chunk reliably from
 *      `capacitor://localhost`.
 *   2. `navigator.share` → Web Share API (Safari mobile, Chrome Android).
 *      No-op on desktop browsers.
 *   3. Clipboard fallback with toast confirmation, using a hidden
 *      `<textarea>` + `document.execCommand('copy')` for ancient browsers
 *      that don't expose `navigator.clipboard`.
 *
 * AbortError on tiers 1+2 means "user cancelled the sheet" — we silently
 * bail without falling through, otherwise dismissing the sheet would
 * surprise the user with a clipboard toast.
 *
 * The hook also encodes the "loop mode → deep-link to single video"
 * vs "default → channel link" share-URL routing that the feed wants:
 *  - loop mode: `${origin}/${username}?v=${id}` (jumps the receiver
 *    straight into that single video, looping)
 *  - default:   `${origin}/${username}` (drops the receiver on the
 *    creator's channel — a discovery surface, not a single clip)
 *
 * Returns: { handleShare(currentVideo, { isLoopMode }) }
 */
export function useFeedShare() {
  const handleShare = useCallback(async (currentVideo, { isLoopMode } = {}) => {
    if (!currentVideo) {
      toast.error("No video to share.");
      return;
    }
    const baseUrl = window.location.origin;
    let shareUrl;
    let shareText;

    // Iter-58.8 (2026-06-02): use the per-video OG card endpoint so
    // X / iMessage / Facebook / Discord render a rich preview with
    // the video's poster + title instead of the generic site-wide
    // earth-bg card. The endpoint is a tiny HTML page with video-
    // specific OG/Twitter-Card meta + a JS redirect so humans land on
    // the real SPA page within ~100ms. Crawlers don't run JS so they
    // only ever read the meta tags. Single-video shares only — for
    // bare-channel shares (`/{username}`) the site-wide card is fine.
    if (isLoopMode) {
      shareUrl = `${baseUrl}/api/share/v/${currentVideo?.id}`;
      shareText = `Watch "${currentVideo?.title}" on GlobalEyes.TV`;
    } else {
      shareUrl = `${baseUrl}/${currentVideo?.username}`;
      shareText = `Check out @${currentVideo?.username} on GlobalEyes.TV`;
    }

    // 3-step share strategy with explicit logging so failures are
    // visible in Safari Web Inspector when running on the iPhone.
    const isNative = Capacitor?.isNativePlatform?.() ?? false;
    console.log(`[share] isNativePlatform=${isNative}, platform=${Capacitor?.getPlatform?.() ?? 'unknown'}`);

    try {
      if (isNative) {
        await CapShare.share({
          title: 'GlobalEyes.TV',
          text: shareText,
          url: shareUrl,
          dialogTitle: 'Share to…',
        });
        console.log('[share] capacitor.share returned OK');
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError' || /cancel/i.test(err?.message || '')) {
        console.log('[share] capacitor.share cancelled by user');
        return;
      }
      console.warn('[share] capacitor failed, falling through to navigator.share:', err);
    }

    try {
      if (navigator.share) {
        await navigator.share({ title: shareText, url: shareUrl });
        console.log('[share] navigator.share returned OK');
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.warn('[share] navigator.share failed:', err);
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard!");
        return;
      }
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      console.warn('[share] clipboard failed:', err);
      toast.error("Couldn't share. Try long-pressing the link.");
    }
  }, []);

  return { handleShare };
}
